import { invenioConfig } from "@config";
import { getSchemaExtensions } from "@forms/rjsf/RJSFExtensionFields";
import _merge from "lodash/merge";

export const schema = (colleges = [], programs = []) => {
  const collegeEnum = colleges.map((college) => college.id);
  const collegeEnumNames = colleges.map((college) => college.name);

  const programEnum = programs.map((program) => program.code);
  const programEnumNames = programs.map((program) => program.name);

  const schemaExtensions = getSchemaExtensions(
    invenioConfig.SPECIAL_COLLECTIONS?.extensions || {}
  );

  const _schema = {
    type: "object",
    required: [
      "title",
      "authors",
      "collection_type",
      "publication_year",
      "sections",
      "call_number",
    ],
    definitions: {
      identifier: {
        properties: {
          scheme: {
            title: "Scheme",
            type: "string",
          },
          value: {
            title: "Value",
            type: "string",
          },
          material: {
            title: "Material",
            type: "string",
          },
        },
        required: ["value", "scheme"],
        type: "object",
      },
    },
    properties: {
      ...schemaExtensions,
      title: {
        title: "Thesis Title",
        type: "string",
      },
      collection_type: {
        title: "Thesis Types",
        type: "string",
        enum: [
          "UNDERGRADUATE_THESIS",
          "MASTER'S_THESIS",
          "DISSERTATION",
          "MANUSCRIPT",
        ],
        enumNames: [
          "Undergraduate Thesis",
          "Master's Thesis",
          "Dissertation",
          "Manuscript",
        ],
      },
      alternative_titles: {
        items: {
          properties: {
            language: {
              title: "Language",
              type: "string",
            },
            type: {
              title: "Type",
              type: "string",
            },
            value: {
              title: "Value",
              type: "string",
            },
          },
          required: ["value"],
          type: "object",
        },
        title: "Alternative titles",
        type: "array",
      },
      languages: {
        items: {
          title: "Language",
          type: "string",
        },
        title: "Languages",
        type: "array",
      },
      edition: {
        title: "Edition",
        type: "string",
      },
      publication_year: {
        title: "Completion Year",
        type: "string",
      },
      number_of_pages: {
        title: "Number of Pages",
        type: "string",
      },
      source: {
        title: "Source",
        type: "string",
      },
      authors: {
        items: {
          properties: {
            full_name: {
              title: "Full Name",
              type: "string",
            },
          },
          required: ["full_name"],

          type: "object",
        },
        minItems: 1,
        title: "Authors",
        type: "array",
      },
      advisers: {
        items: {
          properties: {
            full_name: {
              title: "Full name",
              type: "string",
            },
          },
          required: ["full_name"],

          type: "object",
        },
        title: "Advisers",
        type: "array",
      },
      subjects: {
        items: {
          properties: {
            scheme: {
              title: "Scheme",
              type: "string",
            },
            value: {
              title: "Subject classification",
              type: "string",
            },
          },
          required: ["value"],
          type: "object",
        },
        title: "Subjects",
        type: "array",
      },
      tags: {
        items: {
          title: "Tag",
          type: "string",
        },
        title: "Tags",
        type: "array",
      },
      keywords: {
        items: {
          properties: {
            source: {
              title: "Source",
              type: "string",
            },
            value: {
              title: "Value",
              type: "string",
            },
          },
          required: ["value"],
          type: "object",
        },
        title: "Keywords",
        type: "array",
      },
      imprint: {
        properties: {
          date: {
            title: "Date of Publication",
            type: "string",
          },
          publisher: {
            title: "Publisher",
            type: "string",
          },
        },
        title: "Imprint",
        type: "object",
      },
      identifiers: {
        items: {
          properties: {
            material: {
              title: "Material",
              type: "string",
            },
            scheme: {
              title: "Scheme",
              type: "string",
            },
            value: {
              title: "Value",
              type: "string",
            },
          },
          required: ["value", "scheme"],
        },
        title: "Identifiers",
        type: "array",
      },
      publication_info: {
        items: {
          properties: {
            journal_title: {
              title: "Journal title",
              type: "string",
            },
            journal_volume: {
              title: "Journal volume",
              type: "string",
            },
            journal_issue: {
              title: "Journal issue",
              type: "string",
            },
            pages: {
              title: "Page range",
              type: "string",
            },
            year: {
              title: "Publication Year",
              type: "integer",
            },
            note: {
              title: "Note",
              type: "string",
            },
          },
          type: "object",
        },
        title: "Publication Information",
        type: "array",
      },
      conference_info: {
        items: {
          properties: {
            title: {
              title: "Conference Title",
              type: "string",
            },
            place: {
              title: "Place",
              type: "string",
            },
            dates: {
              title: "Conference Dates",
              type: "string",
            },
            acronym: {
              title: "Acronym",
              type: "string",
            },
            year: {
              title: "Year",
              type: "integer",
            },
          },
          required: ["title", "place"],
          type: "object",
        },
        title: "Conference Information",
        type: "array",
      },
      abstract: {
        title: "Abstract",
        type: "string",
      },
      academic_info: {
        properties: {
          program: {
            title: "Program",
            type: "string",
            enum: programEnum,
            enumNames: programEnumNames,
          },
          department: {
            title: "Department",
            type: "string",
            enum: collegeEnum,
            enumNames: collegeEnumNames,
          },
          campus: {
            title: "Campus",
            type: "string",
          },
        },
        required: ["department", "program"],
        title: "Academic Information",
        type: "object",
      },
      call_number: {
        title: "Thesis Call Number",
        type: "string",
        minLength: 1,
      },

      sections: {
        title: "Library Section",
        type: "string",
        default: "THESIS AND DISSERTATIONS",
      },
      copyrights: {
        items: {
          properties: {
            holder: {
              title: "Copyright Holder",
              type: "string",
            },
            year: {
              title: "Year",
              type: "integer",
            },
            statement: {
              title: "Copyright Notice",
              type: "string",
            },
          },
          type: "object",
        },
        title: "Copyright Information",
        type: "array",
      },
      licenses: {
        items: {
          properties: {
            license: {
              properties: {
                id: {
                  title: "License",
                  type: "string",
                },
              },
              required: ["id"],
              title: "License",
              type: "object",
            },
            material: {
              title: "Material",
              type: "string",
            },
          },
          required: ["license"],
          type: "object",
        },
        title: "License Information",
        type: "array",
      },
      urls: {
        items: {
          properties: {
            value: {
              format: "uri",
              title: "URL",
              type: "string",
            },
            description: {
              title: "Description",
              type: "string",
            },
          },
          required: ["value"],
          type: "object",
        },
        title: "External Resources URLs",
        type: "array",
      },
      internal_notes: {
        items: {
          properties: {
            field: {
              title: "Field",
              type: "string",
            },
            user: {
              title: "User",
              type: "string",
            },
            value: {
              title: "Note",
              type: "string",
            },
          },
          required: ["value"],
        },
        title: "Internal Notes",
        type: "array",
      },
      notes: {
        title: "General Notes",
        type: "string",
      },
    },
  };

  return _merge(_schema, invenioConfig.SPECIAL_COLLECTIONS?.editorSchema || {});
};
